import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../../features/auth/services/auth.service';
import { environment } from '../../../environments/environment';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function addToken(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
  const requestId = Math.random().toString(36).substring(2, 10);
  const headers: Record<string, string> = { 'X-Request-Id': requestId };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return req.clone({ setHeaders: headers });
}

function handle401Error(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      authService.sesionVencida();
      return throwError(() => new Error('No refresh token disponible'));
    }

    return authService.refreshToken(refreshToken).pipe(
      switchMap(response => {
        isRefreshing = false;
        const newAccessToken = response.data.accessToken;
        const newRefreshToken = response.data.refreshToken;

        // Los dos por el service, que es el único que sabe con qué llave
        // guarda cada zona (#746). Con la llave cruda acá, el portal escribía
        // el token nuevo en la casilla del panel: se quedaba con el suyo ya
        // revocado y, de paso, le cambiaba la sesión al usuario interno que
        // tuviera el panel abierto en otra pestaña.
        const rememberMe = authService.isRememberMeActive();
        authService.setToken(newAccessToken, rememberMe);
        authService.setRefreshToken(newRefreshToken);

        refreshTokenSubject.next(newAccessToken);

        // Reintentar el request original con el nuevo token
        return next(addToken(req, newAccessToken));
      }),
      catchError(err => {
        isRefreshing = false;
        // Solo un «no» del servidor termina la sesión. Sin señal o con el servicio caído (0, 5xx) la sesión sigue: la
        // tablet trabaja sin conexión y renueva cuando vuelva. El aviso de sesión vencida lo da errorInterceptor, que ve
        // el 401 de /auth/refresh.
        if (err instanceof HttpErrorResponse && (err.status === 0 || err.status >= 500)) {
          return throwError(() => err);
        }
        authService.sesionVencida();
        return throwError(() => err);
      })
    );
  } else {
    // Ya hay un refresh en curso, esperar a que termine
    return refreshTokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap(token => next(addToken(req, token)))
    );
  }
}

/**
 * Interceptor funcional para añadir el token JWT y manejar renovación silenciosa
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  const isApiUrl = req.url.includes(environment.apiUrl);
  const isAuthUrl = req.url.includes(environment.authServiceUrl);

  if (isApiUrl || isAuthUrl) {
    const clonedReq = addToken(req, token);

    return next(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Si es 401 y no es una petición de refresh ni de login, intentar refresh
        if (
          error.status === 401 &&
          !req.url.includes('/auth/refresh') &&
          !req.url.includes('/auth/login')
        ) {
          return handle401Error(req, next, authService);
        }
        return throwError(() => error);
      })
    );
  }

  return next(req);
};
